// Turn a failed lock/unlock response into a human message.
//
// The modals/buttons used to read a non-existent `data[0].errmsg`, so every failure —
// permission denied, device not configured, gateway offline — collapsed to a generic
// "An error occurred". `getApiError` surfaces the real reason across every response
// shape; here we layer the unlock UX on top: when the reason is missing or generic,
// explain the most common cause — the lock couldn't be reached (offline or no gateway).
import { getApiError } from './getApiError';

export const UNLOCK_UNREACHABLE_MESSAGE =
	"We couldn't reach the lock. It may be offline or not connected to a gateway. Check the gateway is online, then try again.";

// Backend fallbacks that carry no real information — treat them as "unreachable".
const GENERIC_ERRORS = [/^action lock failed$/i, /^an error occurred/i];

export const describeUnlockError = (res) => {
	const detail = getApiError(res);
	if (!detail || GENERIC_ERRORS.some((re) => re.test(detail))) return UNLOCK_UNREACHABLE_MESSAGE;
	return detail;
};

// One door's settled row from a /devices/override response ({ results: [{ success, skipped, reason }] }).
export const overrideRowFromResponse = (deviceID, res) => {
	// Rejected before any hardware (403, validation, unreachable server).
	if (res?.code !== 200 && res?.statusCode !== 200) {
		return { deviceID, status: 'failed', reason: describeUnlockError(res) };
	}
	const [result] = res.data?.results || [];
	if (!result) return { deviceID, status: 'failed', reason: UNLOCK_UNREACHABLE_MESSAGE };
	if (result.skipped) return { deviceID, status: 'skipped', reason: result.reason };
	return {
		deviceID,
		status: result.success ? 'success' : 'failed',
		reason: result.success ? null : result.reason || UNLOCK_UNREACHABLE_MESSAGE
	};
};

// Compose the /devices/override toast from the SETTLED rows of a fan-out. The saga actions one door
// per request and collects a row per door ({ status: 'success'|'failed'|'skipped' }), so unlike the
// old fire-and-forget envelope this can state what actually happened instead of only the verb.
//
// The toast is the summary; the per-door detail (which lock, and why it failed) lives in the modal's
// progress list, which stays open. So the wording counts rather than names — naming one failure out
// of six would read as if the other five were fine.
//
// Skipped doors are NOT failures: they were never attempted (a lift or a camera can't be
// force-opened), so they are reported separately and never turn the toast into an error.
// Returns { type, title, description } for Notification.
export const buildOverrideToast = ({ action = 'Override', results = [] } = {}) => {
	const verb = action.toLowerCase(); // "unlock" | "lock"
	const attempted = results.filter((r) => r.status !== 'skipped');
	const failed = attempted.filter((r) => r.status === 'failed').length;
	const succeeded = attempted.length - failed;
	const skipped = results.length - attempted.length;

	const skippedNote = skipped ? ` ${skipped} skipped (can't be ${verb}ed).` : '';
	const device = (n) => `${n} device${n === 1 ? '' : 's'}`;

	if (!attempted.length) {
		return {
			type: 'warning',
			title: `Nothing to ${verb}`,
			description: `None of the selected devices can be ${verb}ed.`
		};
	}
	if (!failed) {
		return {
			type: 'success',
			title: `${action} successful`,
			description: `${device(succeeded)} ${verb}ed.${skippedNote}`
		};
	}
	if (!succeeded) {
		return {
			type: 'error',
			title: `${action} failed`,
			description: `${device(failed)} failed. Review the list and retry.${skippedNote}`
		};
	}
	return {
		type: 'warning',
		title: `${action} partly successful`,
		description: `${device(succeeded)} ${verb}ed, ${failed} failed. Retry the failures from the list.${skippedNote}`
	};
};
